/**
 * PerformanceAttribution — Iter Feb 2026 R42 · Phase 4.
 *
 * Closed-loop attribution: joins ad spend (from `perf_insights_daily`)
 * with leads (source_meta_campaign_id) and their won-revenue.
 *
 * KPIs surfaced: Spend, Leads, CPL, Won, Win-rate, Revenue, ROAS.
 * Includes a one-click "Backfill cost snapshots" for historical leads.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Wand2, Target, DollarSign, TrendingUp } from "lucide-react";

const fmtMoney = (v) => typeof v === "number" ? `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
const fmtInt = (v) => typeof v === "number" ? v.toLocaleString("en-IN") : "—";

export default function PerformanceAttribution() {
  const { activeWorkspaceId } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [brandId, setBrandId] = useState("all");
  const [brands, setBrands] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/brands`)
      .then(r => setBrands(r.data?.rows || r.data || []))
      .catch(() => {});
  }, [activeWorkspaceId]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (brandId && brandId !== "all") params.brand_id = brandId;
      const { data: d } = await api.get(`/workspaces/${activeWorkspaceId}/perf/attribution`, { params });
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load attribution")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, dateFrom, dateTo, brandId]);
  useEffect(() => { load(); }, [load]);

  const backfill = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/perf/attribution/backfill-cost`);
      toast.success(`Stamped ${data.stamped} lead${data.stamped === 1 ? "" : "s"} with cost snapshot`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Backfill failed")); }
  };

  const t = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="flex flex-col h-full" data-testid="perf-attribution-page">
      <PageHeader
        eyebrow="Performance"
        title="Attribution & ROAS"
        description="Closed-loop attribution: joins ad spend with Meta-lead-ads leads and their won-revenue to give you the only KPI that matters — revenue per rupee spent."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 rounded-none text-xs w-36" data-testid="attr-date-from" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 rounded-none text-xs w-36" data-testid="attr-date-to" />
            <Select value={brandId} onValueChange={setBrandId}>
              <SelectTrigger className="h-9 rounded-none text-xs w-40" data-testid="attr-brand-select"><SelectValue placeholder="All brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-none h-9" onClick={backfill} data-testid="attr-backfill-btn">
              <Wand2 className="w-4 h-4 mr-1.5" /> Backfill cost
            </Button>
            <Button className="rounded-none h-9" onClick={load}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />
      <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-10 py-4 space-y-4">

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            ["Spend",   fmtMoney(t.spend),   DollarSign],
            ["Leads",   fmtInt(t.leads),      Target],
            ["CPL",     fmtMoney(t.cpl),      Target],
            ["Revenue", fmtMoney(t.revenue),  DollarSign],
            ["ROAS",    typeof t.roas === "number" ? `${t.roas}×` : "—", TrendingUp],
          ].map(([label, val, Icon]) => (
            <div key={label} className="border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <Icon className="w-3 h-3" /> {label}
              </div>
              <div className="text-xl font-semibold font-mono">{val}</div>
            </div>
          ))}
        </div>

        <div className="border overflow-hidden">
          <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
            Campaign attribution · {rows.length} campaign{rows.length === 1 ? "" : "s"}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="attr-table">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-neutral-50">
                <tr className="border-b">
                  <th className="text-left px-2 py-1.5">Campaign</th>
                  <th className="text-right px-2 py-1.5">Spend</th>
                  <th className="text-right px-2 py-1.5">Leads</th>
                  <th className="text-right px-2 py-1.5">Won</th>
                  <th className="text-right px-2 py-1.5">Win %</th>
                  <th className="text-right px-2 py-1.5">CPL</th>
                  <th className="text-right px-2 py-1.5">Revenue</th>
                  <th className="text-right px-2 py-1.5">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="py-3 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">
                    No campaigns matched. Ensure ad accounts are linked to brands and leads have `source_meta_campaign_id`.
                  </td></tr>
                )}
                {rows.map(r => (
                  <tr key={r.id} className="border-b hover:bg-neutral-50" data-testid={`attr-row-${r.id}`}>
                    <td className="px-2 py-1.5 truncate max-w-[240px] font-medium" title={r.name}>{r.name}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(r.spend)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtInt(r.leads)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtInt(r.won)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.win_rate ? `${r.win_rate}%` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.cpl ? fmtMoney(r.cpl) : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(r.revenue)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">
                      {r.roas ? `${r.roas}×` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-[11px] text-muted-foreground border-l-2 border-amber-400 pl-2">
          <b>How this works:</b> Each Meta lead carries the campaign id it came from
          (from R40 webhook). When you mark that lead <code>won</code> via
          <code>POST /leads/&#123;lid&#125;/won</code> with a revenue amount, this table
          computes ROAS = Revenue ÷ Spend. Run <b>Backfill cost</b> once to stamp a
          per-lead cost snapshot on historical leads.
        </div>
      </div>
    </div>
  );
}
